import { useState } from 'react';

import { NO_PROJECT_ALERTS_TOOLTIP_CONTENT } from 'modules/campaigns/editor/constants';
import { AlertsCaption } from 'modules/campaigns/lib/components/AlertsCaption';
import { EnabledAlertsList } from 'modules/campaigns/lib/components/EnabledAlertsList';
import { CampaignStateDefinition } from 'modules/graphql';
import { IndentedRowWrapper } from 'modules/workflowNodeEditors/EditorBody.styled';
import { StyledWarningBanner } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/DeliverySection/DeliverySection.styles';
import { ExpandableCheckbox } from 'modules/workflowNodeEditors/editorComponents';
import { Campaign } from 'modules/workflows';
import { useUpdateEffect } from 'react-use';
import { Bell } from 'ui/icons/alert/Bell';
import { colors } from 'ui/theme';
import { TipPlacement } from 'ui/tooltip';
import noop from 'utils/noop';
import { useCampaignAlertJourneyContext } from '../../BaseSendEditor.context/CampaignAlertJourneyContext';
import { isCampaignAlertsEnabled } from '../../BaseSendEditor.utils';

/**
 * The following campaign states do not support modifying Performance Alerts settings
 * in journey campaigns.
 *
 * This logic differs slightly for standalone campaigns. Generally, triggered campaigns
 * should not allow editing in the following states: Archived, Recalled, and Aborted.
 * However, journey campaigns actually hide the entire campaign editor/UI when archived
 * so that state is omitted as it's not really relevant in this case.
 */
export const JOURNEY_CAMPAIGN_ALERT_UPDATES_DISABLED_STATES = [
  CampaignStateDefinition.Aborted,
  CampaignStateDefinition.Recalled,
];

export const getAreJourneyCampaignAlertUpdatesDisallowed = (
  campaignState: CampaignStateDefinition,
) => JOURNEY_CAMPAIGN_ALERT_UPDATES_DISABLED_STATES.includes(campaignState);

export interface CampaignAlertsCheckboxProps {
  /**  Campaign data */
  campaign: Partial<Campaign>;
  /** Is the checkbox disabled */
  isDisabled?: boolean;
  /** Callback when alerts checkbox is updated */
  onChange: (campaignAlertsEnabled: boolean) => void;
  /** Warning text */
  warningText?: string;
}

export const CAMPAIGN_ALERTS_TEST_ID = 'campaign-alerts-checkbox';

export const CampaignAlertCheckbox = ({
  isDisabled = false,
  campaign = {},
  warningText,
  onChange = noop,
}: CampaignAlertsCheckboxProps) => {
  const { alertDetails, hasProjectAlerts } = useCampaignAlertJourneyContext();
  const campaignAlertsEnabled = isCampaignAlertsEnabled(campaign);

  /**
   * The starting state of the checkbox will depend on the value of
   * `campaignAlertsEnabled`. Any existing campaigns before alerts
   * were introduced should start with the checkbox unchecked.
   */
  const [isChecked, setIsChecked] = useState(campaignAlertsEnabled);

  const areAlertUpdatesDisallowed = getAreJourneyCampaignAlertUpdatesDisallowed(
    campaign.campaignState || CampaignStateDefinition.Draft,
  );

  useUpdateEffect(() => {
    onChange(isChecked);
  }, [isChecked]);

  return (
    <ExpandableCheckbox
      caption={<AlertsCaption />}
      id={'campaign-alerts'}
      isChecked={isChecked}
      onEnabledUpdate={setIsChecked}
      label="Alerts"
      labelSupplement={<Bell color={colors.grey60} width={16} height={16} />}
      isDisabled={isDisabled || !hasProjectAlerts || areAlertUpdatesDisallowed}
      hasTooltip={!hasProjectAlerts}
      tooltipContent={NO_PROJECT_ALERTS_TOOLTIP_CONTENT}
      tooltipPlacement={TipPlacement.Left}
    >
      <IndentedRowWrapper>
        <EnabledAlertsList alertDetails={alertDetails} />
      </IndentedRowWrapper>

      {warningText && (
        <StyledWarningBanner data-test="alerts-overlap-warning">{warningText}</StyledWarningBanner>
      )}
    </ExpandableCheckbox>
  );
};
